import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { FaSearch, FaSync } from 'react-icons/fa';
import PaginationTable from '../../components/PaginationTable';
import { useListadoRecordado } from '../../components/useListadoRecordado';
import { useLoading } from '../../components/LoadingContext';
import { MarcaSiNo } from '../../components/SiNo';

// Seguimiento de Egresados (punto 7 de José, 25/09/2026).
// Listado de la Red de egresados (el mismo cálculo que la vista de
// indicadores) con dos acciones por persona: Ver y Actualizar su ficha de
// seguimiento. Lo cargado aquí aparece en Indicadores > Red de Egresados.

const FILTROS_VACIOS = {
  cedula_identidad: '', nombres: '', apellidos: '', centro_id: '',
  con_seguimiento: '', aplica_red: '', trabaja: '', emprende: '',
};

const SI_NO_SIN = [
  { valor: 'si', texto: '✓' },
  { valor: 'no', texto: '✗' },
  { valor: 'sin', texto: 'Sin dato' },
];

const ShowSeguimientoEgresados = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { filters, setFilters, currentPage, setCurrentPage, olvidar } =
    useListadoRecordado('seguimiento-egresados', FILTROS_VACIOS);

  const [personas, setPersonas] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [centrosRed, setCentrosRed] = useState([]);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const limpios = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));

  const cargar = async (page = 1, filtros = filters) => {
    // Buscar pasa el evento del clic como `page`: cualquier cosa que no sea
    // un número es la página 1.
    const pagina = Number.isInteger(page) && page > 0 ? page : 1;
    setCurrentPage(pagina);
    setError('');
    try {
      const { data } = await axios.get('/red-egresados', {
        headers,
        params: { ...limpios(filtros), page: pagina, solo_lista: 1 },
      });
      setPersonas(Array.isArray(data.datos?.data) ? data.datos.data : []);
      setTotalPages(data.datos?.last_page || 1);
      setTotal(data.datos?.total || 0);
      setCentrosRed(data.centrosRed || []);
      if (pagina > (data.datos?.last_page || 1)) {
        cargar(data.datos?.last_page || 1, filtros);
      }
    } catch (e) {
      console.error('Error cargando el seguimiento de egresados:', e);
      setError('No se pudo cargar el listado. Intente de nuevo.');
      setPersonas([]);
    }
  };

  useEffect(() => {
    setLoading(true);
    cargar(currentPage).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiar = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const limpiar = () => {
    olvidar();
    cargar(1, FILTROS_VACIOS);
  };

  const activo = (v) => (v ? 'is-active-filter' : '');

  const selSiNo = (name, placeholder) => (
    <Form.Select name={name} value={filters[name]} onChange={cambiar} className={activo(filters[name])} style={{ width: 'auto' }}>
      <option value="">{placeholder}</option>
      {SI_NO_SIN.map((o) => <option key={o.valor} value={o.valor}>{placeholder}: {o.texto}</option>)}
    </Form.Select>
  );

  const columns = ['Cédula', 'Nombres', 'Apellidos', 'Edad', 'Centros de la Red', 'Horas',
    'Aplica', 'Trabaja', 'Emprende', 'Actualizado', 'Acciones'];

  const renderItem = (p) => (
    <tr key={p.id}>
      <td>{p.cedula_identidad}</td>
      <td>{p.nombres}</td>
      <td>{p.apellidos}</td>
      <td>{p.edad}</td>
      <td>{p.centros}</td>
      <td>{Number(p.horas)}</td>
      <td className="text-center"><MarcaSiNo valor={p.aplica_red} si="Aplica" no="No aplica" /></td>
      <td className="text-center"><MarcaSiNo valor={p.trabaja} /></td>
      <td className="text-center"><MarcaSiNo valor={p.emprende} /></td>
      <td>{p.seguimiento_actualizado || <span className="text-muted">Sin seguimiento</span>}</td>
      <td>
        <div className="d-flex justify-content-around">
          <Button variant="btn btn-info" className="me-2" title="Ver"
            onClick={() => navigate(`/seguimiento-egresados/${p.id}`)}>
            <i className="bi bi-eye-fill"></i>
          </Button>
          <Button variant="btn btn-warning" className="me-2" title="Actualizar"
            onClick={() => navigate(`/seguimiento-egresados/${p.id}/edit`)}>
            <i className="bi bi-pencil-fill"></i>
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="container-fluid mt-2" style={{ fontSize: '0.85rem' }}>
      <div className="col-lg-11 mx-auto d-flex justify-content-center">
        <div className="card-box" style={{ padding: '20px', width: '100%', margin: '0 auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: '10px' }}>
            <h2>Seguimiento de Egresados</h2>
            <div className="d-flex" style={{ gap: '8px' }}>
              <Button variant="info" onClick={() => cargar(currentPage)} title="Recargar"><FaSync /></Button>
              <Button variant="secondary" onClick={limpiar} title="Quitar filtros">Limpiar</Button>
            </div>
          </div>
          <p className="small text-muted mb-3">
            Personas de la Red de egresados ({total}). Con <strong>Actualizar</strong> se registra si aplica a la Red,
            su nivel de instrucción actual, información laboral, emprendimiento, actividades de interés y comentarios.
            Los indicadores se ven en Indicadores &gt; Red de Egresados.
          </p>

          <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: '10px' }}>
            <Form.Control name="cedula_identidad" value={filters.cedula_identidad} onChange={cambiar} placeholder="Cédula"
              style={{ width: '140px' }} className={activo(filters.cedula_identidad)}
              onKeyDown={(ev) => ev.key === 'Enter' && cargar(1)} />
            <Form.Control name="nombres" value={filters.nombres} onChange={cambiar} placeholder="Nombres"
              style={{ width: '160px' }} className={activo(filters.nombres)}
              onKeyDown={(ev) => ev.key === 'Enter' && cargar(1)} />
            <Form.Control name="apellidos" value={filters.apellidos} onChange={cambiar} placeholder="Apellidos"
              style={{ width: '160px' }} className={activo(filters.apellidos)}
              onKeyDown={(ev) => ev.key === 'Enter' && cargar(1)} />
            <Form.Select name="centro_id" value={filters.centro_id} onChange={cambiar} className={activo(filters.centro_id)} style={{ width: 'auto' }}>
              <option value="">Centro de la Red</option>
              {centrosRed.map((c) => <option key={c.id} value={c.id}>{c.descripcion}</option>)}
            </Form.Select>
            <Form.Select name="con_seguimiento" value={filters.con_seguimiento} onChange={cambiar} className={activo(filters.con_seguimiento)} style={{ width: 'auto' }}>
              <option value="">Seguimiento</option>
              <option value="si">Con seguimiento</option>
              <option value="no">Sin seguimiento</option>
            </Form.Select>
            {selSiNo('aplica_red', 'Aplica')}
            {selSiNo('trabaja', 'Trabaja')}
            {selSiNo('emprende', 'Emprende')}
            <Button variant="info" onClick={() => cargar(1)} title="Buscar"><FaSearch /> Buscar</Button>
          </div>

          {error && <div className="text-danger small mb-2">{error}</div>}
          {centrosRed.length === 0 && !error && (
            <div className="alert alert-warning">
              Todavía no hay centros marcados como parte de la Red de egresados (Maestros &gt; Centros).
            </div>
          )}

          <PaginationTable
            data={personas}
            itemsPerPage={12}
            columns={columns}
            renderItem={renderItem}
            currentPage={currentPage}
            onPageChange={(p) => cargar(p)}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default ShowSeguimientoEgresados;
